// src/components/charts/production-management-charts/ProductionTokensByPhaseStacked.jsx
import React, { useMemo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent
} from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../providers/AppProvider';

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
  BarChart,
  CanvasRenderer
]);

/** How many jobs fit in the card before it starts scrolling. */
const VISIBLE_ROWS = 9;

const FALLBACK_PHASE_ORDER = [
  'conductor_isolation',
  'rope',
  'interior_sheat',
  'exterior_sheat',
  'arming',
  'coils',
  'rewinding',
  'packing'
];

function humanizePhaseKey(key) {
  if (!key) return '';
  return String(key)
    .split('_')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getJobLabel(job) {
  return job?.productLabel || job?.jobCode || job?.name || 'Job';
}

/**
 * Culori stabile (nu depind de theme tokens), arată bine pe ambele teme.
 * Dacă ai mai multe faze, se repetă, dar suficient pentru 7–10 faze.
 */
function getPhasePalette(isDark) {
  return isDark
    ? [
        '#60A5FA', // blue-400
        '#22D3EE', // cyan-400
        '#34D399', // emerald-400
        '#A78BFA', // violet-400
        '#FBBF24', // amber-400
        '#FB7185', // rose-400
        '#F472B6', // pink-400
        '#F97316', // orange-500
        '#93C5FD', // blue-300
        '#5EEAD4' // teal-300
      ]
    : [
        '#2563EB', // blue-600
        '#0891B2', // cyan-600
        '#059669', // emerald-600
        '#7C3AED', // violet-600
        '#D97706', // amber-600
        '#E11D48', // rose-600
        '#BE185D', // pink-700
        '#EA580C', // orange-600
        '#1D4ED8', // blue-700
        '#0F766E' // teal-700
      ];
}

function buildMasterPhaseOrder(jobs) {
  // 1) ia ordinea din phasesForGant dacă există (ideal: backend consistent)
  const union = [];
  const seen = new Set();

  for (const job of jobs) {
    const list = Array.isArray(job?.phasesForGant) ? job.phasesForGant : [];
    for (const ph of list) {
      if (!seen.has(ph)) {
        seen.add(ph);
        union.push(ph);
      }
    }
  }

  // 2) fallback: dacă nu ai phasesForGant la nimeni
  if (!union.length) return FALLBACK_PHASE_ORDER;

  // 3) adaugă faze care apar în tokens dar nu-s în phasesForGant (just in case)
  for (const job of jobs) {
    const tokens = job?.tokens ? Object.values(job.tokens) : [];
    for (const t of tokens) {
      const ph = t?.phase;
      if (ph && !seen.has(ph)) {
        seen.add(ph);
        union.push(ph);
      }
    }
  }

  return union;
}

function countTokensByPhase(job) {
  const tokens = job?.tokens ? Object.values(job.tokens) : [];
  const map = {};
  for (const t of tokens) {
    const ph = t?.phase;
    if (!ph) continue;
    map[ph] = (map[ph] || 0) + 1;
  }
  return map;
}

const getOptions = ({ isDark, jobs }) => {
  const phaseOrder = buildMasterPhaseOrder(jobs);
  const palette = getPhasePalette(isDark);

  // Turned on its side for the same reason as ProductionJobsProgress: a product
  // name is a long horizontal word, and thirty of them across the bottom of a
  // card overlap into an unreadable band — worse on a phone, where there is no
  // width to spare. Sorted by how many tokens a job carries, so the biggest
  // pieces of work are the first thing read.
  const rows = jobs
    .map(job => ({ label: getJobLabel(job), counts: countTokensByPhase(job) }))
    .map(row => ({
      ...row,
      total: Object.values(row.counts).reduce((a, b) => a + Number(b || 0), 0)
    }))
    .sort((a, b) => b.total - a.total);

  const labels = rows.map(r => r.label);
  const perJobCounts = rows.map(r => r.counts);
  const scrolls = rows.length > VISIBLE_ROWS;

  const series = phaseOrder.map((phaseKey, idx) => {
    const color = palette[idx % palette.length];
    return {
      name: humanizePhaseKey(phaseKey),
      type: 'bar',
      stack: 'tokens',
      barMaxWidth: 16,
      emphasis: { focus: 'series' },
      itemStyle: {
        color,
        borderRadius: idx === phaseOrder.length - 1 ? [0, 4, 4, 0] : 0
      },
      data: perJobCounts.map(m => Number(m?.[phaseKey] || 0))
    };
  });

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: params => {
        if (!params?.length) return '';
        const title = params[0]?.axisValue ?? 'Job';
        const rows = params
          .filter(p => Number(p?.data || 0) > 0)
          .map(
            p =>
              `<div style="display:flex;justify-content:space-between;gap:14px;">
                 <span>${p.marker} ${p.seriesName}</span>
                 <b>${p.data}</b>
               </div>`
          )
          .join('');

        const total = params.reduce((acc, p) => acc + Number(p?.data || 0), 0);

        return `
          <div style="font-weight:800;margin-bottom:6px;">${title}</div>
          ${rows || `<div style="opacity:.75;">No tokens</div>`}
          <div style="margin-top:6px;display:flex;justify-content:space-between;">
            <span style="opacity:.8;">Total</span><b>${total}</b>
          </div>
        `;
      }
    },
    legend: {
      type: 'scroll',
      top: 0,
      textStyle: {
        color: isDark ? '#E5E7EB' : '#0F172A',
        fontWeight: 600
      }
    },
    xAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: {
        color: isDark ? '#E5E7EB' : '#0F172A',
        fontWeight: 700
      },
      splitLine: {
        lineStyle: { color: isDark ? '#334155' : '#CBD5E1', type: 'dashed' }
      }
    },
    yAxis: {
      type: 'category',
      data: labels,
      // first category at the top, where the biggest job belongs
      inverse: true,
      axisLabel: {
        color: isDark ? '#E5E7EB' : '#0F172A',
        fontWeight: 600,
        formatter: value => {
          const s = String(value || '');
          return s.length > 18 ? `${s.slice(0, 18)}…` : s;
        }
      },
      axisLine: {
        lineStyle: { color: isDark ? '#334155' : '#CBD5E1' }
      },
      axisTick: { show: false }
    },
    series,
    ...(scrolls
      ? {
          dataZoom: [
            { type: 'inside', yAxisIndex: 0, startValue: 0, endValue: VISIBLE_ROWS - 1 },
            {
              type: 'slider',
              yAxisIndex: 0,
              startValue: 0,
              endValue: VISIBLE_ROWS - 1,
              width: 8,
              right: 4,
              showDetail: false,
              brushSelect: false,
              handleSize: 0,
              borderColor: 'transparent',
              fillerColor: isDark ? '#334155' : '#CBD5E1'
            }
          ]
        }
      : {}),
    grid: {
      left: 4,
      right: scrolls ? 24 : 8,
      bottom: 4,
      top: 48,
      containLabel: true
    },
    animation: false
  };
};

const ProductionTokensByPhaseStacked = ({ jobs = [], style }) => {
  const {
    config: { theme }
  } = useAppContext();

  const isDark = theme === 'dark';
  const safeJobs = Array.isArray(jobs) ? jobs : [];

  const option = useMemo(
    () => getOptions({ isDark, jobs: safeJobs }),
    [isDark, safeJobs]
  );

  if (!safeJobs.length) {
    return (
      <div
        style={{
          height: style?.height || 320,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        className="text-700 fw-semibold"
      >
        No jobs to display
      </div>
    );
  }

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      style={style || { height: 320, width: '100%' }}
    />
  );
};

export default ProductionTokensByPhaseStacked;
